import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Check, X, Eye, Loader2, RefreshCw, UserCheck, UserX } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface PremiumRequest {
  id: string;
  assessment_id: string;
  full_name: string | null;
  email: string;
  company: string | null;
  designation: string | null;
  pdf_url: string | null;
  status: string;
  reviewed_at: string | null;
  signed_up_at: string | null;
  created_at: string;
  assessment_title?: string;
  assessment_slug?: string;
}

const fetchPremiumRequests = async (): Promise<PremiumRequest[]> => {
  const { data, error } = await supabase
    .from('premium_access_requests')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;

  const assessmentIds = [...new Set((data || []).map(r => r.assessment_id))];
  const { data: assessments } = await supabase
    .from('assessments')
    .select('id, title, slug')
    .in('id', assessmentIds);

  const assessmentMap = new Map(assessments?.map(a => [a.id, a]) || []);

  return (data || []).map(r => ({
    ...r,
    assessment_title: assessmentMap.get(r.assessment_id)?.title || 'Unknown',
    assessment_slug: assessmentMap.get(r.assessment_id)?.slug || '',
  }));
};

const sendPremiumLink = async (request: PremiumRequest) => {
  const { data: settings } = await supabase
    .from('app_settings')
    .select('site_url')
    .limit(1)
    .single();

  const siteUrl = (settings?.site_url || 'https://maturity-compass.gapstars.net').replace(/\/+$/, '');

  const { error: fnError } = await supabase.functions.invoke('send-premium-link', {
    body: {
      email: request.email,
      fullName: request.full_name || '',
      assessmentTitle: request.assessment_title || '',
      premiumLink: siteUrl,
    },
  });
  if (fnError) throw fnError;
};

export default function PremiumRequests() {
  const queryClient = useQueryClient();
  const [confirmAction, setConfirmAction] = useState<{ type: 'approve' | 'deny' | 'resend'; request: PremiumRequest } | null>(null);

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['premium-access-requests'],
    queryFn: fetchPremiumRequests,
  });

  const approveMutation = useMutation({
    mutationFn: async (request: PremiumRequest) => {
      const { error } = await supabase
        .from('premium_access_requests')
        .update({ status: 'approved', reviewed_at: new Date().toISOString() })
        .eq('id', request.id);
      if (error) throw error;
      await sendPremiumLink(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['premium-access-requests'] });
      queryClient.invalidateQueries({ queryKey: ['premium-requests-count'] });
      toast({ title: 'Approved', description: 'Signup link sent to user.' });
      setConfirmAction(null);
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
      setConfirmAction(null);
    },
  });

  const resendMutation = useMutation({
    mutationFn: async (request: PremiumRequest) => {
      await sendPremiumLink(request);
    },
    onSuccess: () => {
      toast({ title: 'Resent', description: 'A new signup link has been sent.' });
      setConfirmAction(null);
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
      setConfirmAction(null);
    },
  });

  const denyMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('premium_access_requests')
        .update({ status: 'denied', reviewed_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['premium-access-requests'] });
      queryClient.invalidateQueries({ queryKey: ['premium-requests-count'] });
      toast({ title: 'Denied', description: 'Request has been denied.' });
      setConfirmAction(null);
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
      setConfirmAction(null);
    },
  });

  const handleConfirm = () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'approve') {
      approveMutation.mutate(confirmAction.request);
    } else if (confirmAction.type === 'resend') {
      resendMutation.mutate(confirmAction.request);
    } else {
      denyMutation.mutate(confirmAction.request.id);
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'pending': return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending</Badge>;
      case 'approved': return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Approved</Badge>;
      case 'denied': return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Denied</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const signupStatusBadge = (request: PremiumRequest) => {
    if (request.status !== 'approved') return <span className="text-muted-foreground text-sm">—</span>;
    if (request.signed_up_at) {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 gap-1">
          <UserCheck className="h-3 w-3" />
          Signed Up
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 gap-1">
        <UserX className="h-3 w-3" />
        Pending
      </Badge>
    );
  };

  const isActionPending = approveMutation.isPending || denyMutation.isPending || resendMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead>Assessment</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Signup</TableHead>
              <TableHead>Requested</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  No full assessment access requests yet.
                </TableCell>
              </TableRow>
            ) : (
              requests.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.full_name || '—'}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{r.company || '—'}</TableCell>
                  <TableCell>{r.designation || '—'}</TableCell>
                  <TableCell>{r.assessment_title}</TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell>{signupStatusBadge(r)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(r.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {r.pdf_url && (
                        <Button variant="ghost" size="icon" asChild>
                          <a href={r.pdf_url} target="_blank" rel="noopener noreferrer">
                            <Eye className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      {r.status === 'pending' && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => setConfirmAction({ type: 'approve', request: r })}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setConfirmAction({ type: 'deny', request: r })}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {r.status === 'approved' && !r.signed_up_at && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-primary hover:text-primary/80"
                          title="Resend signup link"
                          onClick={() => setConfirmAction({ type: 'resend', request: r })}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'approve' ? 'Approve Request' : confirmAction?.type === 'resend' ? 'Resend Signup Link' : 'Deny Request'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === 'approve'
                ? `Approve the full assessment access request from ${confirmAction.request.full_name || confirmAction.request.email}? A signup link will be emailed to them.`
                : confirmAction?.type === 'resend'
                ? `Send a new signup link to ${confirmAction?.request.full_name || confirmAction?.request.email}? Any previous link will be invalidated.`
                : `Deny the full assessment access request from ${confirmAction?.request.full_name || confirmAction?.request.email}?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActionPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={isActionPending}
              className={confirmAction?.type === 'deny' ? 'bg-destructive hover:bg-destructive/90' : ''}
            >
              {isActionPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {confirmAction?.type === 'approve' ? 'Approve' : confirmAction?.type === 'resend' ? 'Resend' : 'Deny'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
